import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { useUpdateTruckMutation } from "@/store/trucks/apis";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mover, Truck } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SaveIcon, TrashIcon } from "lucide-react";

const TRUCK_LENGTHS = [
  { value: "26", label: "26 ft" },
  { value: "24", label: "24 ft" },
  { value: "22", label: "22 ft" },
  { value: "20", label: "20 ft" },
  { value: "16", label: "16 ft" },
  { value: "15", label: "15 ft" },
  { value: "12", label: "12 ft" },
  { value: "10", label: "10 ft" },
];

interface TruckRowProps {
  truck: Truck & { mover_truck_id?: number };
  mover: Mover;
  deactivated?: boolean;
  onDeactivate?: () => void;
}

const TruckRow: React.FC<TruckRowProps> = ({
  truck,
  mover,
  deactivated,
  onDeactivate,
}) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [updateTruck, { isLoading }] = useUpdateTruckMutation();
  const [truckData, setTruckData] = useState(truck);

  const handleUpdateTruck = async () => {
    if (isLoading) return;

    try {
      // First update the truck
      const updatedTruck = await updateTruck({
        id: truck.id,
        data: {
          name: truckData.name,
          length_ft: truckData.length_ft,
          notes: truckData.notes,
        },
      }).unwrap();

      // Then update the mover to maintain local state
      const updatedMover = {
        ...mover,
        trucks: mover.trucks.map((t) =>
          t.id === truck.id
            ? {
                ...t,
                name: updatedTruck.name,
                length_ft: updatedTruck.length_ft,
                notes: updatedTruck.notes,
              }
            : t
        ),
      };

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Truck updated successfully.",
      });
    } catch (error) {
      console.error("Error updating truck:", error);
      toast({
        title: "Error",
        description: "Failed to update truck. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 items-center mt-4">
      <div className="col-span-3">
        <Input
          value={truckData.name}
          onChange={(e) =>
            setTruckData((prev) => ({ ...prev, name: e.target.value }))
          }
          disabled={deactivated}
        />
      </div>
      <div className="col-span-2">
        <Select
          value={truckData.length_ft.toString()}
          onValueChange={(value) =>
            setTruckData((prev) => ({ ...prev, length_ft: parseInt(value) }))
          }
          disabled={deactivated}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRUCK_LENGTHS.map((length) => (
              <SelectItem key={length.value} value={length.value}>
                {length.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-5">
        <Input
          value={truckData.notes || ""}
          onChange={(e) =>
            setTruckData((prev) => ({ ...prev, notes: e.target.value }))
          }
          disabled={deactivated}
        />
      </div>
      <div className="col-span-2 flex items-center gap-2">
        {!deactivated && (
          <>
            <Button
              variant="link"
              className="text-black"
              onClick={handleUpdateTruck}
            >
              <SaveIcon className="w-4 h-4 mr-1" />
              Save
            </Button>
            <Button
              variant="link"
              className="text-red-700"
              onClick={onDeactivate}
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Deactivate
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const Trucks: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();
  const [updateTruck] = useUpdateTruckMutation();
  const [newTruck, setNewTruck] = useState<Partial<Truck>>({
    name: "",
    length_ft: 26,
    notes: "",
    rental: false,
  });

  const handleAddTruck = async () => {
    if (isLoading) return;
    if (!selectedMover) return;

    try {
      const updatedMover = await updateMoverApi({
        data: {
          mover_trucks_attributes: [
            {
              truck_attributes: {
                name: newTruck.name,
                length_ft: newTruck.length_ft,
                notes: newTruck.notes,
                rental: newTruck.rental,
              },
            },
          ],
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      setNewTruck({
        name: "",
        length_ft: 26,
        notes: "",
        rental: false,
      });

      toast({
        title: "Success",
        description: "Truck added successfully.",
      });
    } catch (error) {
      console.error("Error adding truck:", error);
      toast({
        title: "Error",
        description: "Failed to add truck. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeactivateTruck = async (
    truck: Truck & { mover_truck_id?: number }
  ) => {
    if (isLoading) return;
    if (!selectedMover) return;

    try {
      await updateTruck({
        id: truck.id,
        data: {
          deactivated_at: new Date().toISOString(),
        },
      }).unwrap();

      const updatedMover = {
        ...selectedMover,
        trucks: selectedMover.trucks.map((t) =>
          t.id === truck.id
            ? { ...t, deactivated_at: new Date().toISOString() }
            : t
        ),
      };

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Truck deactivated successfully.",
      });
    } catch (error) {
      console.error("Error deactivating truck:", error);
      toast({
        title: "Error",
        description: "Failed to deactivate truck. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!selectedMover) return null;

  const activeTrucks = selectedMover.trucks.filter(
    (truck) => !truck.deactivated_at
  );
  const deactivatedTrucks = selectedMover.trucks.filter(
    (truck) => truck.deactivated_at
  );

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold">Trucks</h2>
      <div className="mt-4 w-full bg-stone-100 text-black px-2 py-1 text-sm">
        Add New Truck
      </div>

      <div className="mt-4 grid grid-cols-12 gap-4 items-center">
        <div className="col-span-3">
          <Input
            placeholder="Name/Number"
            value={newTruck.name}
            onChange={(e) =>
              setNewTruck((prev) => ({ ...prev, name: e.target.value }))
            }
          />
        </div>
        <div className="col-span-2">
          <Select
            value={newTruck.length_ft?.toString()}
            onValueChange={(value) =>
              setNewTruck((prev) => ({ ...prev, length_ft: parseInt(value) }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Length" />
            </SelectTrigger>
            <SelectContent>
              {TRUCK_LENGTHS.map((length) => (
                <SelectItem key={length.value} value={length.value}>
                  {length.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-5">
          <Input
            placeholder="Notes"
            value={newTruck.notes || ""}
            onChange={(e) =>
              setNewTruck((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
        </div>
        <div className="col-span-2">
          <Button onClick={handleAddTruck}>Add Truck</Button>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-stone-100 text-black px-2 py-1 text-sm">
          Active Trucks ({activeTrucks.length})
        </div>
        {activeTrucks.map((truck) => (
          <TruckRow
            key={truck.id}
            truck={truck}
            mover={selectedMover}
            onDeactivate={() => handleDeactivateTruck(truck)}
          />
        ))}
      </div>

      {deactivatedTrucks.length > 0 && (
        <div className="mt-8">
          <div className="bg-stone-100 text-black px-2 py-1 text-sm">
            Deactivated Trucks ({deactivatedTrucks.length})
          </div>
          {deactivatedTrucks.map((truck) => (
            <TruckRow
              key={truck.id}
              truck={truck}
              mover={selectedMover}
              deactivated
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Trucks;
